/**
 * Utility Functions
 * Common helpers used across the app
 */

import type { NotificationItem } from '../types';

export function dispatchChatSessionLoad(sessionId: string | null): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('kci-load-chat-session', { detail: { sessionId } }),
  );
}

export function dispatchActivityDashboardLoad(agentId: string | null = null): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('kci-load-activity-dashboard', { detail: { agentId } }),
  );
}

/**
 * Safely query select an element
 */
export const qs = (selector: string, context: Document | Element = document) => {
  return context.querySelector(selector);
};

/**
 * Safely query select all elements
 */
export const qsa = (selector: string, context: Document | Element = document) => {
  return context.querySelectorAll(selector);
};

/**
 * Remove a CSS class from all matching elements
 */
export const clearClass = (
  container: Element,
  selector: string,
  className: string
) => {
  container.querySelectorAll(selector).forEach(el => {
    el.classList.remove(className);
  });
};

/**
 * Add a CSS class to all matching elements
 */
export const addClassToAll = (
  container: Element,
  selector: string,
  className: string
) => {
  container.querySelectorAll(selector).forEach(el => {
    el.classList.add(className);
  });
};

/**
 * Toggle a CSS class and return the new state
 */
export const toggleClass = (el: Element, className: string): boolean => {
  return el.classList.toggle(className);
};

/**
 * Show an element by setting display style
 */
export const showEl = (el: HTMLElement | null, displayType: string = '') => {
  if (el) el.style.display = displayType;
};

/**
 * Hide an element by setting display to none
 */
export const hideEl = (el: HTMLElement | null) => {
  if (el) el.style.display = 'none';
};

/**
 * Generate AI Spark SVG markup
 * Uses currentColor for dynamic coloring
 */
export const aiSparkSVG = (size: number = 20): string => {
  return `<svg width="${size}" height="${size}" viewBox="6 6 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.0098 6.00098C16.0098 11.5202 20.4812 15.9944 25.999 16V16.0098C20.4846 16.0154 16.0154 20.4846 16.0098 25.999H16.001C15.9953 20.4812 11.5202 16.0098 6.00098 16.0098H6V16C11.5227 16 16.0009 11.5236 16.001 6.00098V6H16.0098V6.00098Z" fill="currentColor"/>
  </svg>`;
};

/**
 * Clamp a number between min and max
 */
export const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

/**
 * Debounce a function
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const debounce = <T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Throttle a function
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const throttle = <T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    }
  };
};

/**
 * Format a number as a human-readable string
 */
export const formatNumber = (num: number, decimals: number = 0): string => {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Check if a string is empty or whitespace
 */
export const isEmpty = (str: string): boolean => {
  return !str || str.trim().length === 0;
};

/**
 * Relative time for <= 2 hours, otherwise DD/MM/YY, HH:MM AM/PM
 */
export function formatNotificationTimestamp(createdAt: number): string {
  if (createdAt <= 0) return 'Just now';

  const diffMs = Date.now() - createdAt;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMinutes < 120) {
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes === 1) return '1 minute ago';
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    if (diffHours === 1) return '1 hour ago';
    return `${diffHours} hours ago`;
  }

  const date = new Date(createdAt);
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  const h = date.getHours();
  const hh = String(h % 12 || 12).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${dd}/${mm}/${yy}, ${hh}:${min} ${ampm}`;
}

/**
 * Pending approvals are shared agent/prompt items only.
 */
export function isPendingApprovalNotification(item: NotificationItem): boolean {
  if (item.kind !== 'shared') return false;

  if (item.resourceType === 'agent' || item.resourceType === 'prompt') {
    return true;
  }

  return item.verb === 'shared agent' || item.verb === 'shared prompt';
}
